import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "reactstrap";

//Util
import { ICONES_BOTAO } from "../utils/icones";

//Tamanho mínimo da janela ao redimensionar
const LARGURA_MINIMA = 320;
const ALTURA_MINIMA = 200;

//Altura da barra de título (usada quando a janela está minimizada)
const ALTURA_CABECALHO = 36;

//Largura inicial máxima padrão
const LARGURA_INICIAL_MAXIMA = 760;

//Posição e tamanho iniciais: no lado direito da tela, ocupando quase toda a altura.
//Lateral: encostada no canto direito e ocupando toda a altura da janela do navegador
//larguraMaxima: largura inicial máxima (a janela também não passa de 45% da tela)
function getPosicaoInicial(lateral, larguraMaxima = LARGURA_INICIAL_MAXIMA) {
  const largura = Math.max(
    LARGURA_MINIMA,
    Math.min(larguraMaxima, Math.round(window.innerWidth * 0.45)),
  );

  if (lateral) {
    //Área visível sem a barra de rolagem do navegador (o jsdom dos testes não tem: usa o tamanho da janela)
    const larguraVisivel =
      document.documentElement.clientWidth || window.innerWidth;
    const alturaVisivel =
      document.documentElement.clientHeight || window.innerHeight;

    return {
      x: Math.max(0, larguraVisivel - largura),
      y: 0,
      largura,
      altura: Math.max(ALTURA_MINIMA, alturaVisivel),
    };
  }

  const altura = Math.max(ALTURA_MINIMA, window.innerHeight - 100);

  return {
    x: Math.max(0, window.innerWidth - largura - 20),
    y: 60,
    largura,
    altura,
  };
}

/*
 * Janela que fica por cima das telas sem bloquear a tela de baixo (sem fundo escuro e sem prender o foco):
 * o usuário continua digitando na tela que abriu a janela. A janela é movida pela barra de título,
 * redimensionada pelo canto inferior direito e pode ser minimizada.
 * Fica no body (portal), por isso aparece por cima também de um modal.
 * botoes: botões extras na barra de título (ex: download, imprimir).
 * lateral: abre encostada no canto direito, ocupando toda a altura do navegador (ex: posse e conferência,
 * para deixar o máximo da tela que abriu visível ao lado do documento).
 * larguraInicial: (opcional) largura inicial máxima, para a janela com mais conteúdo (ex: a última leitura do documento,
 * com o documento ao lado dos dados lidos). Sem informar, 760px.
 */
function JanelaFlutuante({
  titulo,
  dicaTitulo, //opcional: texto ao passar o mouse no título (ex: o nome do arquivo)
  botoes,
  onFechar,
  lateral = false,
  larguraInicial = LARGURA_INICIAL_MAXIMA,
  children,
}) {
  //### USE REF ###
  //Dados do arrasto ou do redimensionamento em andamento
  const acaoRef = useRef(null);

  //### USE STATE ###
  const [posicao, setPosicao] = useState(() =>
    getPosicaoInicial(lateral, larguraInicial),
  );
  const [minimizada, setMinimizada] = useState(false);
  //Enquanto move ou redimensiona, o conteúdo fica coberto para o PDF (iframe) não "roubar" o movimento do mouse
  const [emMovimento, setEmMovimento] = useState(false);

  //### FUNÇÕES ###
  //Inicia o arrasto (tipo "mover") ou o redimensionamento (tipo "redimensionar")
  function iniciarAcao(event, tipo) {
    //Clique nos botões da barra não move a janela
    if (event.target.closest("button")) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    acaoRef.current = {
      tipo,
      inicioX: event.clientX,
      inicioY: event.clientY,
      posicaoInicial: posicao,
    };
    setEmMovimento(true);
  }

  //Move ou redimensiona conforme o mouse, sem deixar a janela sair da tela
  function moverAcao(event) {
    const acao = acaoRef.current;
    if (!acao) return;

    const deltaX = event.clientX - acao.inicioX;
    const deltaY = event.clientY - acao.inicioY;
    const inicial = acao.posicaoInicial;

    if (acao.tipo == "mover") {
      setPosicao({
        ...inicial,
        x: Math.min(
          Math.max(0, inicial.x + deltaX),
          window.innerWidth - Math.min(inicial.largura, 120),
        ),
        y: Math.min(
          Math.max(0, inicial.y + deltaY),
          window.innerHeight - ALTURA_CABECALHO,
        ),
      });
    }
    //Borda esquerda: a borda direita fica parada; a esquerda vai até o início da tela, respeitando a largura mínima
    else if (acao.tipo == "esquerda") {
      const direita = inicial.x + inicial.largura;
      const x = Math.min(
        Math.max(0, inicial.x + deltaX),
        direita - LARGURA_MINIMA,
      );

      setPosicao({ ...inicial, x, largura: direita - x });
    }
    //Borda direita: só a largura
    else if (acao.tipo == "direita") {
      setPosicao({
        ...inicial,
        largura: Math.max(
          LARGURA_MINIMA,
          Math.min(inicial.largura + deltaX, window.innerWidth - inicial.x),
        ),
      });
    }
    //Borda de baixo: só a altura
    else if (acao.tipo == "baixo") {
      setPosicao({
        ...inicial,
        altura: Math.max(
          ALTURA_MINIMA,
          Math.min(inicial.altura + deltaY, window.innerHeight - inicial.y),
        ),
      });
    }
    //Canto inferior direito: largura e altura
    else {
      setPosicao({
        ...inicial,
        largura: Math.max(
          LARGURA_MINIMA,
          Math.min(inicial.largura + deltaX, window.innerWidth - inicial.x),
        ),
        altura: Math.max(
          ALTURA_MINIMA,
          Math.min(inicial.altura + deltaY, window.innerHeight - inicial.y),
        ),
      });
    }
  }

  //Termina o arrasto ou o redimensionamento
  function finalizarAcao(event) {
    if (!acaoRef.current) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    acaoRef.current = null;
    setEmMovimento(false);
  }

  return createPortal(
    <div
      className="janela-flutuante"
      role="dialog"
      aria-label={titulo}
      //Posição e tamanho mudam ao mover e redimensionar
      style={{
        left: posicao.x,
        top: posicao.y,
        width: posicao.largura,
        height: minimizada ? ALTURA_CABECALHO : posicao.altura,
      }}
    >
      <div
        className="janela-flutuante-cabecalho"
        title="Arraste para mover"
        onPointerDown={(event) => iniciarAcao(event, "mover")}
        onPointerMove={moverAcao}
        onPointerUp={finalizarAcao}
        onPointerCancel={finalizarAcao}
      >
        <span className="janela-flutuante-titulo" title={dicaTitulo}>
          {titulo}
        </span>

        <div className="d-flex align-items-center gap-1">
          {botoes}

          <Button
            name="btnMinimizar"
            size="sm"
            color="light"
            title={minimizada ? "Restaurar" : "Minimizar"}
            onClick={() => setMinimizada(!minimizada)}
          >
            {minimizada ? "▢" : "–"}
          </Button>

          <Button name="btnFechar" size="sm" title="Fechar" onClick={onFechar}>
            {ICONES_BOTAO.FECHAR}
          </Button>
        </div>
      </div>

      {!minimizada && (
        <div className="janela-flutuante-corpo">
          {children}

          {emMovimento && <div className="janela-flutuante-cobertura" />}
        </div>
      )}

      {/* Bordas da esquerda, da direita e de baixo: redimensionam como uma janela comum */}
      {!minimizada &&
        ["esquerda", "direita", "baixo"].map((borda) => (
          <div
            key={borda}
            className={`janela-flutuante-borda janela-flutuante-borda-${borda}`}
            onPointerDown={(event) => iniciarAcao(event, borda)}
            onPointerMove={moverAcao}
            onPointerUp={finalizarAcao}
            onPointerCancel={finalizarAcao}
          />
        ))}

      {!minimizada && (
        <div
          className="janela-flutuante-redimensionar"
          title="Arraste para redimensionar"
          onPointerDown={(event) => iniciarAcao(event, "redimensionar")}
          onPointerMove={moverAcao}
          onPointerUp={finalizarAcao}
          onPointerCancel={finalizarAcao}
        />
      )}
    </div>,
    document.body,
  );
}

export default JanelaFlutuante;
